import { useMemo } from "react";
import {
  Box,
  Chip,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { DataGrid, type GridColDef, type GridRowSelectionModel, type GridRowParams } from "@mui/x-data-grid";
import type { PlatformSummary } from "../types/platform";
import type { StreamSummary } from "../types/streamSummary";
import StreamState, { type StreamStateValue } from "./StreamState";
import { getPlatformIcon } from "./PlatformIcons";
import { normalizeTags } from "../utils/tags";

type PlatformTableRow = {
  id: string;
  name: string;
  type: string;
  model: string;
  color: string;
  tags: string[];
  description: string;
  streams: StreamSummary[];
  platform: PlatformSummary;
};

type PlatformTableProps = {
  platforms: PlatformSummary[];
  streamsByPlatform: Map<string, StreamSummary[]>;
  isStreamsLoading: boolean;
  streamsErrorMessage: string | null;
  getStreamState: (streamId: string) => StreamStateValue | undefined;
  fallbackPlatformColor: string;
  selectedPlatformId: string | null;
  selectedPlatformIds: string[];
  onSelectionChange: (platformId: string | null, selectedIds: string[]) => void;
};

const PlatformTable = ({
  platforms,
  streamsByPlatform,
  isStreamsLoading,
  streamsErrorMessage,
  getStreamState,
  fallbackPlatformColor,
  selectedPlatformId,
  selectedPlatformIds,
  onSelectionChange
}: PlatformTableProps) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const selectionModel = useMemo<GridRowSelectionModel>(() => {
    const ids =
      selectedPlatformIds.length > 0
        ? new Set<string>(selectedPlatformIds)
        : selectedPlatformId
          ? new Set<string>([selectedPlatformId])
          : new Set<string>();
    return { type: "include", ids };
  }, [selectedPlatformId, selectedPlatformIds]);

  const rows = useMemo<PlatformTableRow[]>(
    () =>
      platforms.map((platform) => ({
        id: platform.id,
        name: platform.name,
        type: platform.type,
        model: platform.model?.trim() ?? "",
        color: platform.color?.trim() || fallbackPlatformColor,
        tags: normalizeTags(platform.tags),
        description: platform.description?.trim() ?? "",
        streams: streamsByPlatform.get(platform.id) ?? [],
        platform
      })),
    [platforms, streamsByPlatform, fallbackPlatformColor]
  );

  const columns = useMemo<GridColDef<PlatformTableRow>[]>(() => {
    const allColumns: GridColDef<PlatformTableRow>[] = [
      {
        field: "name",
        headerName: "Platform",
        flex: 1,
        minWidth: 80,
        resizable: true,
        renderCell: (params) => {
          const description = params.row.description || "No description";
          return (
            <Tooltip title={description} placement="top" describeChild>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {params.row.name}
                </Typography>
              </Box>
            </Tooltip>
          );
        }
      },
      {
        field: "type",
        headerName: "Type",
        flex: 0.8,
        minWidth: 120,
        resizable: true,
        renderCell: (params) => {
          const PlatformIcon = getPlatformIcon(params.row.type);
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
              <Chip
                icon={<PlatformIcon fontSize="small" />}
                label={params.row.type.toUpperCase()}
                size="small"
                variant="outlined"
                sx={{
                  "& .MuiChip-icon": { marginLeft: "4px", color: params.row.color },
                  borderColor: params.row.color,
                  color: params.row.color
                }}
              />
            </Box>
          );
        }
      },
      {
        field: "model",
        headerName: "Model",
        flex: 0.5,
        minWidth: 140,
        resizable: true,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {params.row.model}
          </Typography>
        )
      },
      {
        field: "tags",
        headerName: "Tags",
        flex: 1.2,
        minWidth: 160,
        resizable: true,
        renderCell: (params) => {
          const tagsLabel = params.row.tags.join(", ");
          return (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {tagsLabel}
            </Typography>
          );
        }
      },
      {
        field: "streams",
        headerName: "Streams",
        flex: 1.5,
        minWidth: 180,
        resizable: true,
        sortable: false,
        cellClassName: "platform-streams-cell",
        renderCell: (params) => {
          if (streamsErrorMessage) {
            return (
              <Typography variant="body2" color="error">
                {streamsErrorMessage}
              </Typography>
            );
          }
          if (isStreamsLoading) {
            return (
              <Typography variant="body2" color="text.secondary">
                Loading streams...
              </Typography>
            );
          }
          if (params.row.streams.length === 0) {
            return (
              <Typography variant="body2" color="text.secondary">
                No streams assigned.
              </Typography>
            );
          }
          const isCompact = params.row.streams.length > 3;
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: isCompact ? 0.75 : 1,
                rowGap: isCompact ? 0.5 : 0.75,
                flexWrap: "wrap"
              }}
            >
              {params.row.streams.map((stream) => (
                <Box
                  key={stream.id}
                  sx={{ display: "flex", alignItems: "center", gap: isCompact ? 0.35 : 0.5 }}
                >
                  <StreamState state={getStreamState(stream.id)} size={isCompact ? 12 : 18} />
                  <Tooltip
                    title={stream.description?.trim() || "No description provided."}
                    placement="top"
                    arrow
                    disableInteractive
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: isCompact ? "0.75rem" : "0.875rem",
                        lineHeight: isCompact ? 1.2 : 1.4,
                        whiteSpace: "normal"
                      }}
                    >
                      {stream.sensor}
                    </Typography>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          );
        }
      }
    ];

    // Filter out Type and Streams columns on small screens
    return isSmallScreen
      ? allColumns.filter((col) => col.field !== "type" && col.field !== "model" && col.field !== "tags" && col.field !== "streams")
      : allColumns;
  }, [getStreamState, isSmallScreen, isStreamsLoading, streamsErrorMessage]);

  if (!rows.length) {
    return null;
  }

  return (
    <Box sx={{ width: "100%", minWidth: 0, overflowX: "auto" }}>
      <DataGrid
        autoHeight
        density="compact"
        hideFooterSelectedRowCount
        rows={rows}
        columns={columns}
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 25 }
          }
        }}
        checkboxSelection
        disableRowSelectionOnClick
        getRowHeight={() => "auto"}
        onRowClick={(params: GridRowParams<PlatformTableRow>) => {
          onSelectionChange(String(params.id), []);
        }}
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={(model: GridRowSelectionModel) => {
          const allRowIds = rows.map((row) => row.id);
          let ids: string[] = [];
          if (model && typeof model === "object" && "type" in model && "ids" in model) {
            const idsSet = model.ids instanceof Set ? model.ids : new Set(model.ids as Iterable<unknown>);
            if (model.type === "exclude") {
              ids = allRowIds.filter((id) => !idsSet.has(id)).map(String);
            } else {
              ids = Array.from(idsSet).map(String);
            }
          } else if (model && (model as any) instanceof Set) {
            ids = Array.from(model as Set<unknown>).map(String);
          } else if (Array.isArray(model)) {
            ids = (model as unknown[]).map(String);
          }
          const stringIds = ids.map((id) => String(id));
          const primarySelection =
            selectedPlatformId && stringIds.includes(selectedPlatformId)
              ? selectedPlatformId
              : stringIds[stringIds.length - 1] ?? null;
          onSelectionChange(primarySelection, stringIds);
        }}
        sx={{
          minWidth: 640,
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
            py: 0.5
          },
          "& .platform-streams-cell": {
            alignItems: "flex-start",
            whiteSpace: "normal",
            py: 1
          }
        }}
      />
    </Box>
  );
};

export default PlatformTable;
